import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { AppConfigService } from '@core/appconfig.service';
import { UserService } from '@core/user.service';
import { ILessonStep } from '@models/lesson-section';
import { ViewerAsset } from '@models/viewer-asset';
import { FeedbackService } from '@services/feedback/feedback.service';
import { ButtonComponent } from '@shared/button/button.component';
import { DrawingBrushSizes, DrawingColors, DrawingTools } from '@shared/drawing/drawing';
import { FullScreenService } from '@shared/full-screen.service';
import { ViewerAudioPlayerComponent } from '@shared/viewer/viewer-audio-player/viewer-audio-player.component';
import { ViewerBackButtonComponent } from '@shared/viewer/viewer-back-button/viewer-back-button.component';
import { ViewerConfigurationModel } from '@shared/viewer/viewer-configuration-model';
import { ViewerDrawComponent } from '@shared/viewer/viewer-draw/viewer-draw.component';
import {
  ViewerLessonMaterialsComponent
} from '@shared/viewer/viewer-lesson-materials/viewer-lesson-materials.component';
import {
  ViewerNavigationToggleComponent
} from '@shared/viewer/viewer-navigation-toggle/viewer-navigation-toggle.component';
import { ViewerPrintComponent } from '@shared/viewer/viewer-print/viewer-print.component';
import { ViewerBaseComponent } from '@shared/viewer/viewer-shared/viewer-base.component';
import { ViewerZoomComponent } from '@shared/viewer/viewer-zoom/viewer-zoom.component';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { Subscription } from 'rxjs';
import { ViewerAdditionalInfoComponent } from './viewer-additional-info/viewer-additional-info.component';

/**
 * Icons from https://tabler.io/icons
 */
@Component({
  selector: 'zbp-viewer-controls',
  templateUrl: './viewer-controls.component.html',
  styleUrls: ['./viewer-controls.component.scss',],
  encapsulation: ViewEncapsulation.None,
  imports: [
    ViewerAdditionalInfoComponent,
    ViewerDrawComponent,
    ViewerAudioPlayerComponent,
    ViewerNavigationToggleComponent,
    ViewerZoomComponent,
    ViewerPrintComponent,
    ViewerBackButtonComponent,
    ButtonComponent,
    ViewerLessonMaterialsComponent,
  ]
})
export class ViewerControlsComponent extends ViewerBaseComponent implements AfterViewInit, OnDestroy {
  @Input() viewerConfiguration: ViewerConfigurationModel;
  @Input() viewerAssets: ViewerAsset[] = [];
  @Input() isInsideIframe: boolean;
  @Input() isActivityComplete: boolean;
  /**
   * Helpful for when styling changes cannot be controlled by CSS alone.
   *
   * Example: Controlling popover placement.
   */
  @Input() isDesktop: boolean;
  /**
   * Used to determine if we show the lesson materials control.
   * This is used instead of {@link lessonSteps} because the data may take time to load,
   * which causes the control to pop in after the rest of the controls.
   */
  @Input() isLessonNavigation: boolean = false;
  /**
   * Used when viewing materials from the lesson planner.
   * Provides the lesson planner steps as navigation points in the Lesson Materials viewer control.
   */
  @Input() lessonSteps: ILessonStep[] = [];
  @Output() activeTool = new EventEmitter<DrawingTools>();
  @Output() zoomPercent = new EventEmitter<number>();
  @Output() triggerPrint = new EventEmitter<boolean>();
  @Output() clearDrawing = new EventEmitter<boolean>();
  @Output() activeColor = new EventEmitter<DrawingColors>();
  @Output() activeSize = new EventEmitter<DrawingBrushSizes>();

  @ViewChild('assetControls') assetControls: ElementRef<HTMLElement>;
  @ViewChild('viewerZoomComponent') viewerZoomComponent: ViewerZoomComponent;

  isHighlights: boolean;
  isFullScreen: boolean = false;

  private static readonly FIRST_BUTTON_CLASS = 'asset-control-first';
  private static readonly LAST_BUTTON_CLASS = 'asset-control-last';

  private assetControlsObserver: MutationObserver;

  subscriptions: Subscription[] = [];

  // webkitfullscreenchange added for older iOS versions
  private readonly events = ['fullscreenchange', 'webkitfullscreenchange'];
  private readonly onChange = () => {
    this.isFullScreen = this.fullScreenService.isFullscreen();
  };

  constructor(appConfigService: AppConfigService,
              private fullScreenService: FullScreenService,
              public userService: UserService,
              public feedbackService: FeedbackService,) {
    super();
    this.isHighlights = appConfigService.isHighlightsPortalUrl;
  }

  ngAfterViewInit(): void {
    if (this.assetControls?.nativeElement) {
      this.assetControlsObserver = new MutationObserver(() => this.styleFirstAndLastAssetControl());
      this.assetControlsObserver.observe(this.assetControls.nativeElement, { childList: true, subtree: true });

      this.styleFirstAndLastAssetControl();

      this.events.forEach(e => document.addEventListener(e, this.onChange));
    }

    this.isFullScreen = this.fullScreenService.isFullscreen();
  }

  ngOnDestroy(): void {
    this.assetControlsObserver?.disconnect();
    this.events.forEach(e => document.removeEventListener(e, this.onChange));
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  /**
   * Adds a custom class to the first and last zbp-button-component dynamically,
   * allowing the first and last buttons to have rounded borders.
   * This can't be done in CSS due to nesting.
   *
   * All controls in the #assetControls div need to have the "viewer-asset-control-button"
   */
  private styleFirstAndLastAssetControl(): void {
    const host = this.assetControls?.nativeElement;
    if (!host) {
      return;
    }

    const buttons = Array.from(host.querySelectorAll<HTMLElement>('.viewer-asset-control-button'));

    host.querySelectorAll<HTMLElement>(
      `.${ViewerControlsComponent.FIRST_BUTTON_CLASS}, .${ViewerControlsComponent.LAST_BUTTON_CLASS}`
    ).forEach((el) => {
      el.classList.remove(ViewerControlsComponent.FIRST_BUTTON_CLASS, ViewerControlsComponent.LAST_BUTTON_CLASS);
    });

    if (buttons?.length) {
      if (buttons.length > 1) {
        buttons[0].classList.add(ViewerControlsComponent.FIRST_BUTTON_CLASS);
        buttons[buttons.length - 1].classList.add(ViewerControlsComponent.LAST_BUTTON_CLASS);
      } else {
        buttons[0].classList.add(ViewerControlsComponent.LAST_BUTTON_CLASS);
      }

    }
  }

  setActiveDrawingTool(activeDrawingTool: DrawingTools): void {
    this.selectedTool = activeDrawingTool;
    this.activeTool.emit(activeDrawingTool);
  }

  setActiveZoomTool(activeZoomTool: DrawingTools): void {
    this.selectedTool = activeZoomTool;
  }

  setZoomPercent(zoom: number): void {
    this.zoomLevel = zoom;
    this.zoomPercent.emit(zoom);
  }

  printImage(): void {
    this.triggerPrint.emit(true);
  }

  clear() {
    this.clearDrawing.emit(true);
  }

  setActiveColor(color: DrawingColors): void {
    this.activeColor.emit(color);
    this.drawingColor = color;
  }

  setActiveSize(size: DrawingBrushSizes): void {
    this.activeSize.emit(size);
    this.brushSize = size;
  }

  toggleFullScreen(): void {
    this.fullScreenService.toggle();
  }

  pageChanged() {
    this.viewerZoomComponent?.resetZoom();
  }

  openFeedbackForm() {
    this.subscriptions.push(
      this.feedbackService.openFeedbackForm(true).subscribe()
    );
  }

  protected readonly zbpIconNames = zbpIconNames;
}
